import React from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import {Check} from '../../../icons/Check';
import {P} from '../../../layout/Text/Text';
import {Hints} from '../Hints/Hints';

import {useCheckbox} from './useCheckbox';
import {
    getActiveVariables,
    getBaseVariables,
    getDisabledVariables,
    getFocusVariables,
    getHoverVariables
} from './utils';

import type {ValidationFunc} from '@nfq/react-form';

/**
 * Interface representing the props for the `TagCheckbox` component.
 * This component is designed for selecting or deselecting tags within a form.
 */
export interface ComponentProps {
    /**
     * The value associated with this checkbox.
     * This value will be compared against the form's current value to determine if the checkbox should be checked.
     */
    initialValue?: boolean;
    /**
     * Indicates whether the input field should be disabled, making it non-interactive.
     *
     * @default false
     */
    isDisabled?: boolean;
    /**
     * Indicates whether to retain the field value when the component unmounts.
     *
     * @default false
     */
    keepOnUnmount?: boolean;
    /**
     * The label displayed next to the checkbox.
     * This text should describe the checkbox option, making the UI more user-friendly.
     */
    label?: string;
    /**
     * The name of the form field that this checkbox is associated with.
     * This property is used to track the field in the form's data structure.
     */
    name: string;
    /**
     * Callback function triggered when the value in the input field changes.
     * It receives the new value as a boolean.
     */
    onChange?(value: boolean): void;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Checkbox'
     */
    testId?: string;
    /**
     * Optional validation function to validate the input value.
     */
    validation?: ValidationFunc;
}

/**
 * A reusable checkbox form component that integrates with form state management using a custom hook.
 * This component renders a styled checkbox input along with a label. It uses the `useCheckbox` hook
 * to handle the checkbox state and field updates internally. The checkbox's state is initialized with
 * a given value and kept in sync with the form field it represents.
 * This component also supports a `testId` prop for easier test targeting in automated tests.
 *
 * @param props              The component props.
 * @param props.initialValue The initial checked state of the checkbox.
 * @param props.label        The label text displayed next to the checkbox.
 * @param props.name         The unique name used to identify the form field.
 * @param props.testId       A unique test ID used for testing purposes.
 * @returns A rendered checkbox with label and proper form field integration.
 *
 * @example
 * ```tsx
 * <Checkbox name="agreeToTerms" label="I agree to the terms" initialValue={false} />
 * ```
 */
const Checkbox = observer(({
    initialValue = false,
    isDisabled = false,
    keepOnUnmount = false,
    label = '',
    name,
    onChange = () => {},
    testId = 'Checkbox',
    validation = () => null
}: ComponentProps) => {
    const {colors, error, handleChange, value: isChecked} = useCheckbox(
        name,
        initialValue,
        keepOnUnmount,
        validation,
        onChange
    );

    return (
        <InputErrorWrapper data-cy={testId}>
            <InputLabelWrapper data-has-error={error !== null && error.length > 0}>
                <InputIconWrapper>
                    <CheckboxInput
                        checked={isChecked}
                        className={isChecked ? 'active' : ''}
                        data-cy={`${testId}-input-field`}
                        disabled={isDisabled}
                        name={name}
                        type="checkbox"
                        value={name}
                        onChange={handleChange}
                    />
                    <CheckboxCheckedIcon color1={colors.neutral0} />
                </InputIconWrapper>
                {label && <Label data-cy={`${testId}-label`}>{label}</Label>}
            </InputLabelWrapper>
            <Hints baseTestId={testId} hints={error} hintType="error" paddingLeft={0} />
        </InputErrorWrapper>
    );
});

Checkbox.displayName = 'Checkbox';

export {Checkbox};

const InputErrorWrapper = styled.div``;

const InputLabelWrapper = styled.label`
    ${getBaseVariables}

    align-items: center;
    cursor: var(--cursor);
    display: flex;
    gap: ${spacing(1)};
    width: max-content;


    &:has(input:focus-visible) {
        ${getFocusVariables};
    }

    &:has(input.active) {
        ${getActiveVariables};
    }

    &:has(input[disabled]) {
        ${getDisabledVariables};
    }

    &:hover {
        ${getHoverVariables}
    }
`;

const InputIconWrapper = styled.div`
    height: 1.6rem;
    position: relative;
    width: 1.6rem;
`;

const CheckboxInput = styled.input`
    appearance: none;
    background-color: var(--input-background-color);
    border: var(--input-border);
    border-radius: 4px;
    cursor: var(--cursor);
    height: 1.6rem;
    outline: var(--input-outline);
    transition: background-color 0.3s ease-in-out, border-color 0.3s ease-in-out, outline 0.3s ease-in-out;
    width: 1.6rem;

    &:focus-visible {
        outline-offset: 2px;
    }
`;

const CheckboxCheckedIcon = styled(Check)`
    cursor: var(--cursor);
    display: var(--icon-display);

    left: 50%;
    pointer-events: none;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
`;

const Label = styled(P)`
    color: var(--label-font-color);
    transition: color 0.3s ease-in-out;
    white-space: nowrap;
`;